import React, { useState } from "react";
import { Search, Car, User, Check, Loader2, AlertCircle } from "lucide-react";
import { adminApi } from "../../lib/api";

/**
 * ClientLookupCard — Reg / VIN typeahead that auto-fills the flat client &
 * vehicle fields on the parent form when a match is found.
 *
 * props:
 *   value: { client_id, vehicle_id }
 *   onMatch: (client, vehicle) => void   // called with picked or {}
 *   registration: string (current form value — used as starting query)
 */
export const ClientLookupCard = ({ value, onMatch, registration = "", vin = "" }) => {
  const [reg, setReg] = useState(registration);
  const [vinQ, setVinQ] = useState(vin);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);  // {client, vehicle} | {none:true}
  const [error, setError] = useState(null);

  const searchByReg = async () => {
    if (!reg.trim() && !vinQ.trim()) return;
    setBusy(true); setError(null);
    try {
      const params = {};
      if (reg.trim()) params.registration = reg.trim();
      if (vinQ.trim()) params.vin = vinQ.trim();
      const res = await adminApi.lookupClientByVehicle(params);
      if (res.client) {
        setResult(res);
        onMatch(res.client, res.vehicle);
      } else {
        setResult({ none: true });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    setResult(null); setError(null); onMatch(null, null);
  };

  const linked = value?.client_id && result?.client?.id === value.client_id;

  return (
    <div className="border-2 border-dashed border-[#f28a16]/50 bg-[#fff8ea] rounded-md p-4" data-testid="client-lookup-card">
      <div className="flex items-center gap-2">
        <Car size={16} className="text-[#f28a16]" />
        <h3 className="font-display uppercase tracking-widest text-xs font-bold text-[#f28a16]">
          Look up client by registration or VIN
        </h3>
      </div>
      <p className="text-xs text-[#7a5510] mt-1 mb-3">
        Auto-fills the client &amp; vehicle sections below if this reg is in our register. Skip if it&apos;s a first-time client.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
        <input
          value={reg}
          onChange={(e) => setReg(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchByReg())}
          placeholder="Registration (e.g. ABC 123 GP)"
          className="h-10 px-3 border border-[#cfd7db] rounded-md text-sm bg-white font-mono uppercase"
          data-testid="lookup-reg-input"
        />
        <input
          value={vinQ}
          onChange={(e) => setVinQ(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchByReg())}
          placeholder="VIN (17 chars)"
          className="h-10 px-3 border border-[#cfd7db] rounded-md text-sm bg-white font-mono uppercase"
          data-testid="lookup-vin-input"
        />
        <button
          type="button"
          onClick={searchByReg}
          disabled={busy || (!reg.trim() && !vinQ.trim())}
          className="h-10 px-4 bg-[#20282c] text-white text-xs font-display uppercase tracking-widest rounded-md inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60"
          data-testid="lookup-search-btn"
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Look up
        </button>
      </div>

      {/* Result */}
      {result?.client && (
        <div
          className={`mt-3 p-3 rounded-md border ${linked ? "border-[#10b981] bg-[#ecfdf5]" : "border-[#cfd7db] bg-white"}`}
          data-testid="lookup-result"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-xs font-display uppercase tracking-widest text-[#065f46]">
                <Check size={13} /> Match found
              </div>
              <div className="mt-1 font-bold text-[#111619]">
                <User size={13} className="inline mr-1 -mt-0.5 text-[#f28a16]" />
                {result.client.full_name || result.client.first_name}
              </div>
              <div className="text-xs text-[#3f4b52]">
                {[result.client.mobile, result.client.email].filter(Boolean).join(" · ") || "no contact info"}
              </div>
              {result.vehicle && (
                <div className="mt-1 text-xs">
                  <span className="inline-flex items-center gap-1 border border-[#cfd7db] px-2 py-0.5 rounded-sm">
                    <Car size={11} className="text-[#f28a16]" />
                    <b className="font-mono">{result.vehicle.registration}</b>
                    {result.vehicle.make_model && <span className="text-[#3f4b52]">· {result.vehicle.make_model}</span>}
                    {result.vehicle.year && <span className="text-[#3f4b52]">· {result.vehicle.year}</span>}
                  </span>
                </div>
              )}
            </div>
            <button
              type="button" onClick={clear}
              className="text-xs text-[#3f4b52] hover:underline"
              data-testid="lookup-unlink-btn"
            >
              Unlink
            </button>
          </div>
        </div>
      )}
      {result?.none && (
        <div className="mt-3 p-3 rounded-md border border-[#fef08a] bg-[#fefce8] text-xs text-[#7a5510] flex items-center gap-2" data-testid="lookup-no-match">
          <AlertCircle size={13} />
          No client on file for that reg / VIN. Fill in the client &amp; vehicle sections below and this will be saved as a new client when you create the job.
        </div>
      )}
      {error && (
        <div className="mt-3 text-xs text-[#7f1d1d]">{error}</div>
      )}
    </div>
  );
};

export default ClientLookupCard;
